import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'src/analytics/KatalLogger';
import { AccessRequestEntity } from 'src/models/AccessRequestModel';
import { Status } from 'src/models/AuthContextModels';
import * as accessRequestApi from '../api/accessRequestApi';
import { CreateAccessRequestInput } from '../api/accessRequestApi';

interface AccessRequestState {
  /** All requests relevant to the current user (beneficiary view). */
  myRequests: AccessRequestEntity[];
  myRequestsLoadingStatus: Status;
  /** All requests the current user submitted (requester view). */
  mySubmissions: AccessRequestEntity[];
  mySubmissionsLoadingStatus: Status;
  /** Pending requests for BGs the user is authorized to approve (admin view). */
  pendingRequests: AccessRequestEntity[];
  pendingRequestsLoadingStatus: Status;
}

export const initialAccessRequestState: AccessRequestState = {
  myRequests: [],
  myRequestsLoadingStatus: Status.NotInitiated,
  mySubmissions: [],
  mySubmissionsLoadingStatus: Status.NotInitiated,
  pendingRequests: [],
  pendingRequestsLoadingStatus: Status.NotInitiated
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

/**
 * Fetch requests where the caller is the beneficiary.
 */
export const fetchMyAccessRequests = createAsyncThunk(
  'accessRequest/fetchMyAccessRequests',
  async (_, { rejectWithValue }) => {
    try {
      return await accessRequestApi.listMyAccessRequests();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch my access requests';
      logger.error('Failed to fetch my access requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch requests the caller submitted (for self or on behalf of others).
 */
export const fetchMyAccessRequestSubmissions = createAsyncThunk(
  'accessRequest/fetchMyAccessRequestSubmissions',
  async (_, { rejectWithValue }) => {
    try {
      return await accessRequestApi.listMyAccessRequestSubmissions();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch my submissions';
      logger.error('Failed to fetch my submissions', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch pending requests for BGs the caller is authorized to manage.
 */
export const fetchPendingAccessRequests = createAsyncThunk(
  'accessRequest/fetchPendingAccessRequests',
  async (filter: accessRequestApi.AccessRequestFilter | undefined, { rejectWithValue }) => {
    try {
      return await accessRequestApi.listAccessRequests({ statuses: ['Pending'], ...filter });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch pending requests';
      logger.error('Failed to fetch pending requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Submit one or more access requests.
 */
export const submitAccessRequests = createAsyncThunk(
  'accessRequest/submitAccessRequests',
  async (requests: CreateAccessRequestInput[], { rejectWithValue }) => {
    try {
      const result = await accessRequestApi.submitAccessRequests(requests);
      if (result.errors.length > 0) {
        const errorMessages = result.errors.map((e) => `${e.user_alias} (${e.code}): ${e.message}`).join('; ');
        if (result.results.length === 0) {
          return rejectWithValue(errorMessages);
        }
        // Partial success — log errors but don't reject
        logger.warn('Partial success submitting access requests', { errors: result.errors });
      }
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to submit access requests';
      logger.error('Failed to submit access requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Cancel one or more pending access requests.
 */
export const cancelAccessRequests = createAsyncThunk(
  'accessRequest/cancelAccessRequests',
  async (requestIds: string[], { rejectWithValue }) => {
    try {
      const result = await accessRequestApi.cancelAccessRequests(requestIds);
      if (result.errors.length > 0) {
        const errorMessages = result.errors.map((e) => `${e.request_id} (${e.code}): ${e.message}`).join('; ');
        if (result.results.length === 0) {
          return rejectWithValue(errorMessages);
        }
        logger.warn('Partial success cancelling access requests', { errors: result.errors });
      }
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to cancel access requests';
      logger.error('Failed to cancel access requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Approve one or more pending access requests with specified segments.
 */
export const approveAccessRequests = createAsyncThunk(
  'accessRequest/approveAccessRequests',
  async (requests: accessRequestApi.ApproveAccessRequestItem[], { rejectWithValue }) => {
    try {
      const result = await accessRequestApi.approveAccessRequests(requests);
      if (result.errors.length > 0) {
        const errorMessages = result.errors.map((e) => `${e.request_id} (${e.code}): ${e.message}`).join('; ');
        if (result.results.length === 0) {
          return rejectWithValue(errorMessages);
        }
        logger.warn('Partial success approving access requests', { errors: result.errors });
      }
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to approve access requests';
      logger.error('Failed to approve access requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Reject one or more pending access requests with reason.
 */
export const rejectAccessRequests = createAsyncThunk(
  'accessRequest/rejectAccessRequests',
  async (requests: { request_id: string; rejection_reason: string }[], { rejectWithValue }) => {
    try {
      const result = await accessRequestApi.rejectAccessRequests(requests);
      if (result.errors.length > 0) {
        const errorMessages = result.errors.map((e) => `${e.request_id} (${e.code}): ${e.message}`).join('; ');
        if (result.results.length === 0) {
          return rejectWithValue(errorMessages);
        }
        logger.warn('Partial success rejecting access requests', { errors: result.errors });
      }
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to reject access requests';
      logger.error('Failed to reject access requests', { error });
      return rejectWithValue(message);
    }
  }
);

/**
 * Clone access from one user to another across business groups.
 */
export const cloneUserAccess = createAsyncThunk(
  'accessRequest/cloneUserAccess',
  async (
    params: { sourceAlias: string; targetAlias: string; targetName: string; dataClassificationIds?: number[] },
    { rejectWithValue }
  ) => {
    try {
      const result = await accessRequestApi.cloneUserAccess(
        params.sourceAlias,
        params.targetAlias,
        params.targetName,
        params.dataClassificationIds
      );
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to clone user access';
      logger.error('Failed to clone user access', { error });
      return rejectWithValue(message);
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const accessRequestSlice = createSlice({
  name: 'accessRequest',
  initialState: initialAccessRequestState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // My Requests (beneficiary)
      .addCase(fetchMyAccessRequests.pending, (state) => {
        state.myRequestsLoadingStatus = Status.Loading;
      })
      .addCase(fetchMyAccessRequests.fulfilled, (state, action: PayloadAction<AccessRequestEntity[]>) => {
        state.myRequestsLoadingStatus = Status.Completed;
        state.myRequests = action.payload;
      })
      .addCase(fetchMyAccessRequests.rejected, (state) => {
        state.myRequestsLoadingStatus = Status.Failed;
      })
      // My Submissions (requester)
      .addCase(fetchMyAccessRequestSubmissions.pending, (state) => {
        state.mySubmissionsLoadingStatus = Status.Loading;
      })
      .addCase(fetchMyAccessRequestSubmissions.fulfilled, (state, action: PayloadAction<AccessRequestEntity[]>) => {
        state.mySubmissionsLoadingStatus = Status.Completed;
        state.mySubmissions = action.payload;
      })
      .addCase(fetchMyAccessRequestSubmissions.rejected, (state) => {
        state.mySubmissionsLoadingStatus = Status.Failed;
      })
      // Pending Requests (admin)
      .addCase(fetchPendingAccessRequests.pending, (state) => {
        state.pendingRequestsLoadingStatus = Status.Loading;
      })
      .addCase(fetchPendingAccessRequests.fulfilled, (state, action: PayloadAction<AccessRequestEntity[]>) => {
        state.pendingRequestsLoadingStatus = Status.Completed;
        state.pendingRequests = action.payload;
      })
      .addCase(fetchPendingAccessRequests.rejected, (state) => {
        state.pendingRequestsLoadingStatus = Status.Failed;
      })
      // After submit/cancel/approve/reject — refresh relevant lists
      .addCase(submitAccessRequests.fulfilled, (state) => {
        // Caller should dispatch fetchMyAccessRequestSubmissions after
      })
      .addCase(cancelAccessRequests.fulfilled, (state) => {
        // Caller should dispatch fetchMyAccessRequests/fetchMyAccessRequestSubmissions after
      })
      .addCase(approveAccessRequests.fulfilled, (state) => {
        // Caller should dispatch fetchPendingAccessRequests after
      })
      .addCase(rejectAccessRequests.fulfilled, (state) => {
        // Caller should dispatch fetchPendingAccessRequests after
      });
  }
});

export default accessRequestSlice.reducer;
